import { AlertTriangle, ChevronRight, UserRound } from 'lucide-react';
import type { HeldEquipment } from '../../types/equipment';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { cn } from '../../utils/cn';

interface HeldEquipmentPanelProps {
  items: HeldEquipment[];
  onOpen: (equipmentId: number) => void;
}

/**
 * "Issued to you" — the equipment the signed-in person is holding, whether a
 * standing assignment or a loan with a return date.
 *
 * Renders nothing when they hold nothing: most people on a farm hold nothing,
 * and an empty "Issued to you" box on every visit is noise.
 */
export default function HeldEquipmentPanel({ items, onOpen }: HeldEquipmentPanelProps) {
  const orgTz = useOrgTimezone();
  if (items.length === 0) return null;

  return (
    <section className="bg-violet-50 border border-violet-100 rounded-xl p-3 space-y-2" aria-label="Issued to you">
      <h2 className="text-sm font-semibold text-violet-800 flex items-center gap-1.5">
        <UserRound className="h-4 w-4" />
        Issued to you
      </h2>
      <ul className="space-y-1.5">
        {items.map(item => (
          <li key={item.assignment_id}>
            <button
              type="button"
              onClick={() => onOpen(item.id)}
              className="w-full text-left bg-white rounded-lg border border-violet-100 px-3 py-2 flex items-center gap-2 hover:shadow-sm transition-shadow"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-neutral-800 break-words">{item.name}</p>
                <p className="text-xs text-neutral-500 flex flex-wrap gap-x-2">
                  {item.due_back_on ? (
                    <span className={cn(item.overdue && 'text-red-600 font-medium')}>
                      {item.overdue ? 'Overdue — was due back ' : 'Due back '}
                      {formatLocalDateInTz(item.due_back_on, orgTz)}
                    </span>
                  ) : (
                    <span>Yours until returned</span>
                  )}
                  {item.maintenance_due_count > 0 && (
                    <span className="text-amber-700 inline-flex items-center gap-0.5">
                      <AlertTriangle className="h-3 w-3" />
                      {item.maintenance_due_count} service{item.maintenance_due_count === 1 ? '' : 's'} due
                    </span>
                  )}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
